import { z } from 'zod';

import { EntityRootSchema } from './pack.js';
import {
  EnumValueDeclarationSchema,
  LocalizedStringSchema,
  RequiredLocalizedNameSchema,
  ValueKindSchema,
} from './predicate.js';

/**
 * The typed-artifact domain for LLM-led interactive authoring (ADR-0008 §1,
 * §3). The commit-bearing state of an authoring session is always a **typed
 * artifact** — the proposed op(s) in progress — carried under a common
 * envelope:
 *
 *   AuthoringArtifact = { kind, target, proposed, diff, provenance }
 *
 * `kind ∈ trait | predicate | entity | fact-batch | type | calendar | …`
 * (ADR-0008 §3). The session machinery is generic; the artifact is typed,
 * with a per-kind `proposed` schema mirroring its define op(s). **Traits are
 * the first build** (ADR-0008 §3) — they are multi-op and compositional (a
 * trait embeds its member predicates), the forcing function that gets the
 * abstraction right.
 *
 * Everything here is JSON-serialisable (no Maps / Dates / class instances):
 * the artifact lives in the client Model AND is persisted to the Campaign DO
 * scratch store (ADR-0008 §2), so it must round-trip losslessly through
 * `JSON.stringify`.
 */

// ── target ────────────────────────────────────────────────────────────────

/**
 * What the artifact commits *against*. Opaque ids never change (ADR-0004 §1),
 * so refining an existing definition is authored as a **new** define op through
 * a **new** artifact — never a mutation of the old id. `existing_id` is
 * therefore a provenance / grounding pointer, not an in-place edit target:
 *
 *   - `null`     — a brand-new definition (the common trait case; the id is
 *     minted at commit).
 *   - `<id>`     — the session is grounded on an existing definition (used for
 *     "clone-and-refine" UX and provenance linkage).
 *
 * A bare string id keeps the pointer JSON-serialisable and kind-agnostic; the
 * per-kind commit dispatch knows how to interpret it.
 */
export const AuthoringTargetSchema = z
  .object({
    existing_id: z.string().min(1).nullable(),
  })
  .strict();
export type AuthoringTarget = z.infer<typeof AuthoringTargetSchema>;

// ── provenance ──────────────────────────────────────────────────────────────

/**
 * Which session + turn produced this artifact snapshot (ADR-0008 §2 — a
 * committed definition "remembers how it was authored"). Both ids are opaque
 * strings; the session id is the DO-scoped authoring session, the turn id
 * identifies the single refine turn whose output this snapshot is.
 */
export const AuthoringProvenanceSchema = z
  .object({
    session_id: z.string().min(1),
    turn_id: z.string().min(1),
  })
  .strict();
export type AuthoringProvenance = z.infer<typeof AuthoringProvenanceSchema>;

// ── trait `proposed` ────────────────────────────────────────────────────────

/**
 * A member predicate of a proposed trait (ADR-0008 §3 — "artifacts embed
 * sub-artifacts: a trait-artifact embeds its member predicates"). A member is
 * EITHER:
 *
 *   - `existing` — a reference to an already-minted predicate the system handed
 *     the model (a bare `pred_…` id string, mirroring `TraitDeclaration.
 *     predicates: string[]`). The model never types an id — it only re-uses one
 *     the server gave it (ADR-0004 §7 / ADR-0008 §4).
 *   - `new` — a *named* new-predicate proposal. On commit the server mints its
 *     opaque `pred_…` id and appends a `define_predicate` op before the
 *     `define_trait` (ADR-0008 §3/§4). Fields mirror `PredicateDeclaration` so
 *     the commit dispatch maps cleanly.
 *
 * `cardinality` is an authoring-level knob (`single | many`) the commit
 * dispatch translates to the real `PredicateDeclaration.uniqueness_key`
 * (ADR-0005 §2): `single → []` (at most one segment ever), `many → ['object']`
 * (one segment per distinct object value). Kept simple at the authoring surface
 * so the GM/LLM reason in "single vs multi-valued" terms, not raw
 * uniqueness-key tuples.
 */
export const ProposedTraitMemberSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('existing'),
      /** An existing predicate id (`pred_…`), as `TraitDeclaration.predicates` stores it. */
      id: z.string().min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal('new'),
      /** Mandatory localized display name → maps to `PredicateDeclaration.label`. */
      name: RequiredLocalizedNameSchema,
      /** Optional LLM-grounding / tooltip blurb → `PredicateDeclaration.description`. */
      description: LocalizedStringSchema.optional(),
      /** Accepted value kinds → `PredicateDeclaration.value_kind` (≥1). */
      value_kind: z.array(ValueKindSchema).min(1).readonly(),
      /**
       * The named options for an `enum` member (ADR-0008 — categorical /
       * non-numeric states). Mirrors `PredicateDeclaration.enum_values`
       * (`EnumValueDeclaration[]` — an opaque `enm_…` id + a mandatory localized
       * `label`), so the commit dispatch attaches it verbatim and the auditor's
       * "enum without enum_values" schema rule passes. Present only when
       * `value_kind` includes `'enum'`; the server mints each option's id (the
       * LLM never types one — ADR-0004 §1).
       */
      enum_values: z.array(EnumValueDeclarationSchema).readonly().optional(),
      /**
       * The allowed entity roots for a `ref` member (ADR-0008 — the value-kind
       * authoring registry's `ref` extra). Maps to `PredicateDeclaration.
       * ref_roots` at commit (`value-kind-authoring.ts` `toDeclarationFields`).
       * Present only when `value_kind` includes `'ref'`; a closed subset of the
       * entity roots (the LLM never invents a root token — the registry filters).
       */
      ref_types: z.array(EntityRootSchema).min(1).readonly().optional(),
      /** Authoring-level multiplicity → mapped to `uniqueness_key` at commit. */
      cardinality: z.enum(['single', 'many']),
      /** Stale-write merge posture → `PredicateDeclaration.merge`. */
      merge: z.enum(['rebase', 'lww']),
    })
    .strict(),
]);
export type ProposedTraitMember = z.infer<typeof ProposedTraitMemberSchema>;

/**
 * The `proposed` body of a `trait` artifact — mirrors `TraitDeclaration`
 * (`name → label`, `applies_to`, member predicates) so the commit dispatch
 * assembles `define_predicate × N + define_trait` directly. `name` accepts a
 * bare string or a localized record (both satisfy the mandatory-default-locale
 * rule, ADR-0004 §1). `members` may be empty during early exploratory turns and
 * fills in as the artifact takes shape.
 */
export const ProposedTraitSchema = z
  .object({
    name: RequiredLocalizedNameSchema,
    description: LocalizedStringSchema.optional(),
    applies_to: z.array(EntityRootSchema).min(1).readonly(),
    members: z.array(ProposedTraitMemberSchema).readonly(),
  })
  .strict();
export type ProposedTrait = z.infer<typeof ProposedTraitSchema>;

// ── diff ────────────────────────────────────────────────────────────────────

/**
 * A single top-level field edit vs the previous artifact snapshot (e.g.
 * `name`, `description`, `applies_to`). `before` / `after` are `unknown`
 * (any JSON-serialisable value) and OPTIONAL — an absent `before` means the
 * field was newly set, an absent `after` means it was cleared.
 */
export const ArtifactFieldChangeSchema = z
  .object({
    path: z.string().min(1),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
  })
  .strict();
export type ArtifactFieldChange = z.infer<typeof ArtifactFieldChangeSchema>;

/**
 * A per-member change vs the previous snapshot (ADR-0008 §1 — "every turn's
 * output is a reviewable diff"). `ref` is a stable-ish handle for the member
 * (an existing member's `pred_…` id, or a provisional key for a new member so
 * the UI can track it across turns before an id is minted). For `changed`,
 * `field_changes` carries the per-field edits (name, value_kind, cardinality,
 * merge, …).
 */
export const ArtifactMemberChangeSchema = z
  .object({
    change: z.enum(['added', 'removed', 'changed']),
    ref: z.string().min(1),
    field_changes: z.array(ArtifactFieldChangeSchema).readonly().optional(),
  })
  .strict();
export type ArtifactMemberChange = z.infer<typeof ArtifactMemberChangeSchema>;

/**
 * The diff of an artifact vs the previous turn's snapshot: top-level field
 * edits + per-member add/remove/change. A JSON-serialisable structure the
 * authoring surface renders as the live "what changed this turn" review. The
 * first materialisation of an artifact carries every field as a change from an
 * empty base.
 */
export const ArtifactDiffSchema = z
  .object({
    field_changes: z.array(ArtifactFieldChangeSchema).readonly(),
    member_changes: z.array(ArtifactMemberChangeSchema).readonly(),
  })
  .strict();
export type ArtifactDiff = z.infer<typeof ArtifactDiffSchema>;

// ── the envelope ────────────────────────────────────────────────────────────

/**
 * The `trait` variant of the `AuthoringArtifact` envelope — the first (and
 * currently only) built `kind`. Future kinds (`predicate`, `entity`,
 * `fact-batch`, `type`, `calendar`, …) are added as further members of
 * {@link AuthoringArtifactSchema}; the envelope shape (`kind`, `target`,
 * `proposed`, `diff`, `provenance`) is invariant across kinds — only
 * `proposed` is per-kind typed.
 */
export const TraitAuthoringArtifactSchema = z
  .object({
    kind: z.literal('trait'),
    target: AuthoringTargetSchema,
    proposed: ProposedTraitSchema,
    diff: ArtifactDiffSchema,
    provenance: AuthoringProvenanceSchema,
  })
  .strict();
export type TraitAuthoringArtifact = z.infer<typeof TraitAuthoringArtifactSchema>;

/**
 * `AuthoringArtifact` (ADR-0008 §3) — the typed-artifact envelope discriminated
 * on `kind`. Extensible by addition: a new authoring kind ships a per-kind
 * `proposed` schema + a variant here, and inherits the whole session /
 * streaming / commit machinery for free. Only the `trait` variant is built now.
 */
export const AuthoringArtifactSchema = z.discriminatedUnion('kind', [
  TraitAuthoringArtifactSchema,
]);
export type AuthoringArtifact = z.infer<typeof AuthoringArtifactSchema>;

/** The set of built artifact kinds. Grows as new `kind`s land (ADR-0008 §3). */
export const AUTHORING_ARTIFACT_KINDS = ['trait'] as const;
export type AuthoringArtifactKind = (typeof AUTHORING_ARTIFACT_KINDS)[number];
